import { NavLink, Outlet, useNavigate } from 'react-router';
import { useLiveWorkspace, useMe, useWorkspaceId, useWorkspaceInfo } from '../api/hooks.ts';
import { useSession } from '../auth/session.tsx';
import { DictationProvider } from '../dictation/Dictation.tsx';
import { Activity, Agents, Folder, ICON, Meeting, Members, Mic, Note, Search, Settings, SignOut, Site, Trash } from '../icons.ts';

const NAV = [
  { to: 'files', label: 'Files', icon: Folder },
  { to: 'notes', label: 'Notes', icon: Note },
  { to: 'voice-notes', label: 'Voice notes', icon: Mic },
  { to: 'meetings', label: 'Meetings', icon: Meeting },
  { to: 'sites', label: 'Sites', icon: Site },
  { to: 'search', label: 'Search', icon: Search },
  { to: 'trash', label: 'Trash', icon: Trash },
  { to: 'activity', label: 'Activity', icon: Activity },
  { to: 'agents', label: 'Connected agents', icon: Agents },
  { to: 'members', label: 'Members', icon: Members },
  { to: 'settings', label: 'Settings', icon: Settings },
] as const;

export function Shell() {
  const ws = useWorkspaceId();
  const me = useMe();
  const info = useWorkspaceInfo(ws);
  const live = useLiveWorkspace(ws);
  const session = useSession();
  const navigate = useNavigate();
  const workspaces = me.data?.workspaces ?? [];

  return (
    <div className="app">
      <aside className="sidebar" aria-label="Workspace navigation">
        <div className="brand">Mystiq</div>
        <div className="workspace-switch">
          {workspaces.length > 1 ? (
            <select className="select" aria-label="Workspace" value={ws} onChange={(event) => navigate(`/w/${event.target.value}/files`)}>
              {workspaces.map((workspace) => (
                <option key={workspace.id} value={workspace.id}>
                  {workspace.name}
                </option>
              ))}
            </select>
          ) : (
            <p className="workspace-name">{info.data?.name ?? ' '}</p>
          )}
          {info.data && <p className="meta">You are {info.data.role === 'viewer' ? 'a viewer' : `an ${info.data.role}`}</p>}
        </div>
        <nav className="nav">
          {NAV.map(({ to, label, icon: Icon }) => (
            <NavLink key={to} to={to} className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}>
              <Icon size={ICON.nav} stroke={ICON.stroke} />
              {label}
            </NavLink>
          ))}
        </nav>
        <div className="sidebar-footer">
          {!live && <p className="meta" role="status"><span className="state">Offline.</span> Reconnecting…</p>}
          <p className="meta">{me.data?.user.displayName ?? me.data?.user.email ?? ''}</p>
          <button className="button button-inline" onClick={session.signOut}>
            <SignOut size={ICON.inline} stroke={ICON.stroke} />
            Sign out
          </button>
        </div>
      </aside>
      <main className="main">
        <div className="sheet">
          {/* Refused here only for a known viewer: while the workspace is still loading, the server decides (it refuses a viewer in the same words). */}
          <DictationProvider key={ws} ws={ws} canWrite={info.data?.role !== 'viewer'}>
            <Outlet />
          </DictationProvider>
        </div>
      </main>
    </div>
  );
}
